import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { listUser } from '../../redux/slices/user.slice';
import { listTache, createTache, updateTache, deleteTache } from '../../redux/slices/tache.slice';
import {
  Alert,
  Autocomplete,
  Avatar,
  AvatarGroup,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Skeleton,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import PageContainer from '../../components/container/PageContainer';
import Breadcrumb from '../../layouts/full-layout/breadcrumb/Breadcrumb';
import TacheDetails from './TacheDetails';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import MessageOutlinedIcon from '@mui/icons-material/MessageOutlined';
import SubjectIcon from '@mui/icons-material/Subject';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';

/* -------------------------------------------------------------------------- */
/*  Constants                                                                  */
/* -------------------------------------------------------------------------- */

const typeTaches = [
  { value: 'cmd-a-valider', label: 'Commande à valider', color: '#FFB84D' },
  { value: 'cmd-a-valider-cq', label: 'Commande à valider CQ', color: '#FF8C00' },
  { value: 'cmd-a-saisir', label: 'Commande à saisir', color: '#4CAF50' },
  { value: 'cmd-a-ettiqueter', label: 'Commande à étiqueter', color: '#00BCD4' },
  { value: 'cmd-a-envoyer', label: 'Commande à envoyer', color: '#2196F3' },
  { value: 'cmd-a-receptionner', label: 'Commande à réceptionner', color: '#673AB7' },
  { value: 'cmd-a-livrer', label: 'Commande à livrer', color: '#F44336' },
  { value: 'cmd-a-resoudre', label: 'Commande à résoudre', color: '#b03727' },
  { value: 'cmd-a-debloquer', label: 'Commande à débloquer', color: '#ca4f2d' },
  { value: 'autre', label: 'Autre', color: '#B0BEC5' },
];
const TYPE_BY_VALUE = Object.fromEntries(typeTaches.map((t) => [t.value, t]));

const tacheStatus = {
  EN_ATTENTE: 'en-attente',
  EN_COURS: 'en-cours',
  TRAITE: 'traite',
  ANNULE: 'annule',
};

const STATUS_META = {
  [tacheStatus.EN_ATTENTE]: { label: 'En attente', color: '#c7ab1c' },
  [tacheStatus.EN_COURS]: { label: 'En cours', color: '#1b83ac' },
  [tacheStatus.TRAITE]: { label: 'Traité', color: '#159073' },
  [tacheStatus.ANNULE]: { label: 'Annulé', color: '#8a8f98' },
};

const SORTS = [
  { value: 'urgent', label: 'Urgentes d’abord' },
  { value: 'recent', label: 'Plus récentes' },
  { value: 'old', label: 'Plus anciennes' },
];

const PAGE_SIZE = 20;
const BCrumb = [{ to: '/app', title: 'Home' }, { title: 'Tâches' }];

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                    */
/* -------------------------------------------------------------------------- */

function formatDate(value, withTime = false) {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  });
}

const isOpen = (t) => t.status !== tacheStatus.TRAITE && t.status !== tacheStatus.ANNULE;

function isOverdue(t) {
  if (!t.is_urgent || !t.date_urgent || !isOpen(t)) return false;
  const d = new Date(t.date_urgent);
  if (Number.isNaN(d.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return d < today;
}

const fullName = (u) => `${u?.nom ?? ''} ${u?.prenom ?? ''}`.trim();
const initials = (u) => `${u?.nom?.[0] ?? ''}${u?.prenom?.[0] ?? ''}`.toUpperCase();

/* -------------------------------------------------------------------------- */
/*  Container (data + actions)                                                 */
/* -------------------------------------------------------------------------- */

const TacheListContainer = () => {
  const dispatch = useDispatch();
  const { loading, error } = useSelector((state) => state.tache_store);
  const { sel_sw_mg } = useSelector((state) => state.ui_store);
  const { loggedUser } = useSelector((state) => state.auth_store);

  const [lst_user, set_lst_user] = useState([]);
  const [lst_tache, set_lst_tache] = useState([]);
  const [sel_tache, set_sel_tache] = useState(null);
  const [show_form, set_show_form] = useState(false);
  const [show_details, set_show_details] = useState(false);
  const [to_delete, set_to_delete] = useState(null);
  const [first_load, set_first_load] = useState(true);
  const [toast, set_toast] = useState({ open: false, message: '', severity: 'success' });

  const notify = useCallback(
    (message, severity = 'success') => set_toast({ open: true, message, severity }),
    [],
  );

  const load_data = useCallback(() => {
    return dispatch(listTache())
      .unwrap()
      .then((res) => {
        const list = Array.isArray(res) ? res : [];
        set_lst_tache(list);
        // keep the opened task in sync without relying on a stale closure
        set_sel_tache((prev) => (prev ? (list.find((el) => el?.idTache === prev.idTache) ?? prev) : prev));
      })
      .catch(() => notify('Impossible de charger les tâches.', 'error'))
      .finally(() => set_first_load(false));
  }, [dispatch, notify]);

  // users only need to be loaded once
  useEffect(() => {
    dispatch(listUser())
      .unwrap()
      .then((res) => set_lst_user(Array.isArray(res) ? res : []))
      .catch(() => notify('Impossible de charger les utilisateurs.', 'error'));
  }, [dispatch, notify]);

  // tasks reload when the selected magasin changes (and on mount)
  useEffect(() => {
    load_data();
  }, [sel_sw_mg, load_data]);

  const run = (promise, successMessage, after) =>
    promise
      .unwrap()
      .then(() => {
        after?.();
        notify(successMessage);
        return load_data();
      })
      .catch((e) => notify(typeof e === 'string' ? e : 'Une erreur est survenue.', 'error'));

  const onCreateTache = (data) =>
    data &&
    run(
      dispatch(
        createTache({
          ...data,
          createdBy: loggedUser ? `${loggedUser?.nom} ${loggedUser?.prenom}` : '',
        }),
      ),
      'Tâche créée.',
      () => set_show_form(false),
    );

  const onUpdateTache = (data, message = 'Tâche mise à jour.') =>
    data && run(dispatch(updateTache(data)), message, () => set_show_form(false));

  const onDeleteTache = (id) =>
    id && run(dispatch(deleteTache(id)), 'Tâche supprimée.', () => set_to_delete(null));

  const onShowForm = (data) => {
    set_sel_tache(data);
    set_show_form(true);
  };
  const onCloseForm = () => {
    set_sel_tache(null);
    set_show_form(false);
  };
  const onShowDetails = (data) => {
    set_sel_tache(data);
    set_show_details(true);
  };
  const onCloseDetails = () => {
    set_sel_tache(null);
    set_show_details(false);
  };

  return (
    <PageContainer title="Les Tâches" description="">
      <Breadcrumb title="" items={BCrumb} />

      <TacheList
        lst_tache={lst_tache}
        lst_user={lst_user}
        first_load={first_load}
        loading={loading}
        onShowForm={onShowForm}
        onShowDetails={onShowDetails}
        onUpdateTache={onUpdateTache}
        onAskDelete={set_to_delete}
      />

      <TacheForm
        show={show_form}
        onClose={onCloseForm}
        lst_user={lst_user}
        onCreateTache={onCreateTache}
        onUpdateTache={onUpdateTache}
        sel_tache={sel_tache}
        loading={loading}
        error={error}
        sel_sw_mg={sel_sw_mg}
      />

      <TacheDetails
        show={show_details}
        onClose={onCloseDetails}
        tache={sel_tache}
        onUpdate={onUpdateTache}
        onEdit={onShowForm}
        onDelete={onDeleteTache}
      />

      <Dialog open={!!to_delete} onClose={() => set_to_delete(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Supprimer cette tâche ?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            La tâche #{to_delete?.idTache} sera supprimée définitivement. Cette action est
            irréversible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => set_to_delete(null)} color="inherit">
            Garder la tâche
          </Button>
          <Button
            onClick={() => onDeleteTache(to_delete?.idTache)}
            color="error"
            variant="contained"
            disabled={loading}
          >
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={3500}
        onClose={() => set_toast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          variant="filled"
          severity={toast.severity}
          onClose={() => set_toast((t) => ({ ...t, open: false }))}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};

/* -------------------------------------------------------------------------- */
/*  Form                                                                       */
/* -------------------------------------------------------------------------- */

const EMPTY_FORM = { users: [], description: '', type: 'autre', is_urgent: false, date_urgent: '' };

const TacheForm = ({
  show,
  onClose,
  sel_tache,
  onCreateTache,
  onUpdateTache,
  lst_user,
  loading,
  error,
  sel_sw_mg,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [values, setValues] = useState(EMPTY_FORM);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!show) return;
    setTouched(false);
    setValues(
      sel_tache
        ? {
            users: Array.isArray(sel_tache.users) ? sel_tache.users : [],
            description: sel_tache.description ?? '',
            type: sel_tache.type ?? 'autre',
            is_urgent: !!sel_tache.is_urgent,
            date_urgent: sel_tache.date_urgent ? String(sel_tache.date_urgent).substring(0, 10) : '',
          }
        : EMPTY_FORM,
    );
  }, [sel_tache, show]);

  const set = (patch) => setValues((v) => ({ ...v, ...patch }));
  const usersError = touched && !values.users.length;
  const dateError = touched && values.is_urgent && !values.date_urgent;

  const onSubmit = () => {
    setTouched(true);
    if (!values.users.length || (values.is_urgent && !values.date_urgent)) return;
    const payload = { ...values, date_urgent: values.is_urgent ? values.date_urgent : '' };
    if (sel_tache) onUpdateTache({ ...payload, idTache: sel_tache.idTache });
    else onCreateTache({ ...payload, magasin: sel_sw_mg });
  };

  return (
    <Dialog open={show} onClose={onClose} fullScreen={fullScreen} fullWidth maxWidth="sm">
      <DialogTitle>{sel_tache ? `Modifier la tâche #${sel_tache.idTache}` : 'Nouvelle tâche'}</DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {typeof error === 'string' ? error : 'Une erreur est survenue.'}
          </Alert>
        )}

        <Stack spacing={2.5} sx={{ pt: 0.5 }}>
          <Autocomplete
            multiple
            disableCloseOnSelect
            disabled={loading}
            options={lst_user ?? []}
            value={values.users}
            isOptionEqualToValue={(o, v) => o?.idUser === v?.idUser}
            getOptionLabel={(el) => `${fullName(el)} (${el?.email})`}
            onChange={(_, newValue) => set({ users: newValue })}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip size="small" label={fullName(option)} {...getTagProps({ index })} />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Assigné à"
                placeholder="Nom, prénom ou email"
                error={usersError}
                helperText={usersError ? 'Choisissez au moins un utilisateur.' : ' '}
              />
            )}
          />

          <TextField
            select
            label="Type de tâche"
            value={values.type}
            disabled={loading}
            onChange={(e) => set({ type: e.target.value })}
          >
            {typeTaches.map((t) => (
              <MenuItem key={t.value} value={t.value}>
                <Box
                  component="span"
                  sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: t.color, mr: 1.5 }}
                />
                {t.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Description"
            multiline
            minRows={3}
            disabled={loading}
            value={values.description}
            onChange={(e) => set({ description: e.target.value })}
          />

          <Stack direction="row" spacing={2} alignItems="flex-start">
            <FormControlLabel
              sx={{ mt: 1, minWidth: 120 }}
              control={
                <Switch
                  checked={values.is_urgent}
                  onChange={(e) => set({ is_urgent: e.target.checked })}
                  color="warning"
                />
              }
              label="Urgent"
            />
            {values.is_urgent && (
              <TextField
                fullWidth
                type="date"
                label="Échéance"
                disabled={loading}
                InputLabelProps={{ shrink: true }}
                value={values.date_urgent}
                onChange={(e) => set({ date_urgent: e.target.value })}
                error={dateError}
                helperText={dateError ? 'Indiquez une date.' : ' '}
              />
            )}
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={loading} color="inherit">
          Fermer
        </Button>
        <Button variant="contained" onClick={onSubmit} disabled={loading}>
          {sel_tache ? 'Enregistrer' : 'Créer la tâche'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* -------------------------------------------------------------------------- */
/*  Summary (résumé)                                                           */
/* -------------------------------------------------------------------------- */

const StatCard = ({ label, value, color, active, onClick, icon }) => {
  const theme = useTheme();
  return (
    <Paper
      component="button"
      type="button"
      onClick={onClick}
      aria-pressed={active}
      variant="outlined"
      sx={{
        width: '100%',
        textAlign: 'left',
        cursor: 'pointer',
        p: 2,
        font: 'inherit',
        color: 'inherit',
        bgcolor: active ? alpha(color, 0.12) : 'background.paper',
        borderColor: active ? color : 'divider',
        borderRadius: 2,
        transition: 'background-color .2s, border-color .2s, transform .15s',
        '&:hover': { borderColor: color, transform: 'translateY(-1px)' },
        '&:focus-visible': { outline: `2px solid ${color}`, outlineOffset: 2 },
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        {icon}
      </Stack>
      <Typography variant="h3" sx={{ mt: 0.5, color, fontWeight: 700, lineHeight: 1.1 }}>
        {value}
      </Typography>
    </Paper>
  );
};

const Summary = ({ tasks, filters, setFilters, loading }) => {
  const stats = useMemo(() => {
    const s = { total: tasks.length, urgent: 0, overdue: 0 };
    Object.values(tacheStatus).forEach((k) => (s[k] = 0));
    tasks.forEach((t) => {
      if (s[t.status] !== undefined) s[t.status] += 1;
      if (t.is_urgent && isOpen(t)) s.urgent += 1;
      if (isOverdue(t)) s.overdue += 1;
    });
    return s;
  }, [tasks]);

  const counted = stats.total - stats[tacheStatus.ANNULE];
  const pct = counted ? Math.round((stats[tacheStatus.TRAITE] / counted) * 100) : 0;
  const toggleStatus = (status) =>
    setFilters((f) => ({ ...f, status: f.status === status ? '' : status, urgentOnly: false }));

  if (loading) {
    return (
      <Grid container spacing={2} mb={3}>
        {[...Array(4)].map((_, i) => (
          <Grid item xs={6} md={3} key={i}>
            <Skeleton variant="rounded" height={92} />
          </Grid>
        ))}
      </Grid>
    );
  }

  const segments = [tacheStatus.TRAITE, tacheStatus.EN_COURS, tacheStatus.EN_ATTENTE];

  return (
    <Box mb={3}>
      <Grid container spacing={2}>
        <Grid item xs={6} md={3}>
          <StatCard
            label={STATUS_META[tacheStatus.EN_ATTENTE].label}
            value={stats[tacheStatus.EN_ATTENTE]}
            color={STATUS_META[tacheStatus.EN_ATTENTE].color}
            active={filters.status === tacheStatus.EN_ATTENTE}
            onClick={() => toggleStatus(tacheStatus.EN_ATTENTE)}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label={STATUS_META[tacheStatus.EN_COURS].label}
            value={stats[tacheStatus.EN_COURS]}
            color={STATUS_META[tacheStatus.EN_COURS].color}
            active={filters.status === tacheStatus.EN_COURS}
            onClick={() => toggleStatus(tacheStatus.EN_COURS)}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label={STATUS_META[tacheStatus.TRAITE].label}
            value={stats[tacheStatus.TRAITE]}
            color={STATUS_META[tacheStatus.TRAITE].color}
            active={filters.status === tacheStatus.TRAITE}
            onClick={() => toggleStatus(tacheStatus.TRAITE)}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label={stats.overdue ? `Urgentes (${stats.overdue} en retard)` : 'Urgentes ouvertes'}
            value={stats.urgent}
            color="#d9541e"
            icon={<WarningAmberIcon sx={{ fontSize: 18, color: '#d9541e' }} />}
            active={filters.urgentOnly}
            onClick={() => setFilters((f) => ({ ...f, urgentOnly: !f.urgentOnly, status: '' }))}
          />
        </Grid>
      </Grid>

      {/* Progress bar: the one distinctive element — completion at a glance */}
      <Paper variant="outlined" sx={{ mt: 2, p: 2, borderRadius: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="baseline" mb={1}>
          <Typography variant="subtitle1" fontWeight={600}>
            {pct}% des tâches traitées
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {stats[tacheStatus.TRAITE]} sur {counted}
            {stats[tacheStatus.ANNULE] ? ` · ${stats[tacheStatus.ANNULE]} annulée(s)` : ''}
          </Typography>
        </Stack>
        <Box
          role="img"
          aria-label={`${pct}% traitées`}
          sx={{
            display: 'flex',
            height: 10,
            borderRadius: 5,
            overflow: 'hidden',
            bgcolor: (t) => alpha(t.palette.text.primary, 0.08),
          }}
        >
          {segments.map((s) => (
            <Box
              key={s}
              sx={{
                width: counted ? `${(stats[s] / counted) * 100}%` : 0,
                bgcolor: STATUS_META[s].color,
                transition: 'width .5s ease',
                '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
              }}
            />
          ))}
        </Box>
      </Paper>
    </Box>
  );
};

/* -------------------------------------------------------------------------- */
/*  Task card                                                                  */
/* -------------------------------------------------------------------------- */

const TacheCard = React.memo(({ tache, onShowDetails, onShowForm, onUpdateTache, onAskDelete }) => {
  const status = STATUS_META[tache.status] ?? STATUS_META[tacheStatus.EN_ATTENTE];
  const type = TYPE_BY_VALUE[tache.type] ?? TYPE_BY_VALUE.autre;
  const users = Array.isArray(tache.users) ? tache.users : [];
  const overdue = isOverdue(tache);
  const comments = tache.commentaires?.length ?? 0;
  const stop = (fn) => (e) => {
    e.stopPropagation();
    fn();
  };

  return (
    <Paper
      variant="outlined"
      onClick={() => onShowDetails(tache)}
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onShowDetails(tache)}
      sx={{
        position: 'relative',
        cursor: 'pointer',
        p: 2,
        pl: 2.5,
        borderRadius: 2,
        overflow: 'hidden',
        transition: 'border-color .2s, box-shadow .2s',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: '0 auto 0 0',
          width: 4,
          bgcolor: status.color,
        },
        '&:hover, &:focus-visible': {
          borderColor: status.color,
          boxShadow: (t) => `0 4px 16px ${alpha(t.palette.common.black, 0.08)}`,
          outline: 'none',
        },
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="subtitle2" sx={{ color: type.color, fontWeight: 700 }}>
          #{tache.idTache}
        </Typography>
        <Chip
          size="small"
          label={type.label}
          variant="outlined"
          sx={{ borderColor: type.color, borderRadius: 1 }}
        />
        {tache.commande?.ref && (
          <Typography variant="body2" fontWeight={600}>
            {tache.commande.ref}
          </Typography>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Chip
          size="small"
          label={status.label}
          sx={{
            bgcolor: alpha(status.color, 0.14),
            color: status.color,
            fontWeight: 600,
            borderRadius: 1,
          }}
        />
      </Stack>

      <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 1.5 }}>
        <SubjectIcon
          fontSize="small"
          sx={{ mt: '2px', color: tache.description ? 'text.secondary' : 'text.disabled' }}
        />
        <Typography
          variant="body2"
          sx={{
            color: tache.description ? 'text.primary' : 'text.disabled',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {tache.description || 'Aucune description'}
        </Typography>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        useFlexGap
        spacing={1}
        sx={{ mt: 1.5 }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
          {users.length ? (
            <Tooltip title={users.map(fullName).join(', ')}>
              <AvatarGroup
                max={4}
                sx={{ '& .MuiAvatar-root': { width: 26, height: 26, fontSize: 12 } }}
              >
                {users.map((u) => (
                  <Avatar key={u.idUser} sx={{ bgcolor: 'primary.main' }}>
                    {initials(u)}
                  </Avatar>
                ))}
              </AvatarGroup>
            </Tooltip>
          ) : (
            <Typography variant="body2" color="text.disabled">
              Personne d’assigné
            </Typography>
          )}

          {tache.is_urgent && (
            <Chip
              size="small"
              icon={overdue ? <EventBusyIcon /> : <WarningAmberIcon />}
              label={
                tache.date_urgent
                  ? `${overdue ? 'En retard' : 'Urgent'} · ${formatDate(tache.date_urgent)}`
                  : 'Urgent'
              }
              sx={{
                bgcolor: overdue ? '#fbd5d0' : '#fde3d6',
                color: '#a13d1f',
                fontWeight: 600,
                borderRadius: 1,
                '& .MuiChip-icon': { color: '#a13d1f' },
              }}
            />
          )}

          {comments > 0 && (
            <Stack direction="row" spacing={0.5} alignItems="center" color="text.secondary">
              <MessageOutlinedIcon sx={{ fontSize: 16 }} />
              <Typography variant="body2">{comments}</Typography>
            </Stack>
          )}

          <Typography variant="caption" color="text.secondary">
            {formatDate(tache.created_at)} · {tache.createdBy || 'CRM'}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={0.5} alignItems="center" onClick={(e) => e.stopPropagation()}>
          {tache.status === tacheStatus.EN_ATTENTE && (
            <Button
              size="small"
              variant="contained"
              startIcon={<PlayArrowRoundedIcon />}
              onClick={stop(() =>
                onUpdateTache({ idTache: tache.idTache, status: tacheStatus.EN_COURS }, 'Tâche démarrée.'),
              )}
              sx={{ bgcolor: STATUS_META[tacheStatus.EN_COURS].color }}
            >
              Démarrer
            </Button>
          )}
          {tache.status === tacheStatus.EN_COURS && (
            <Button
              size="small"
              variant="contained"
              startIcon={<CheckRoundedIcon />}
              onClick={stop(() =>
                onUpdateTache({ idTache: tache.idTache, status: tacheStatus.TRAITE }, 'Tâche traitée.'),
              )}
              sx={{ bgcolor: STATUS_META[tacheStatus.TRAITE].color }}
            >
              Terminer
            </Button>
          )}
          <Tooltip title="Modifier">
            <IconButton size="small" aria-label="Modifier" onClick={stop(() => onShowForm(tache))}>
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Supprimer">
            <IconButton
              size="small"
              color="error"
              aria-label="Supprimer"
              onClick={stop(() => onAskDelete(tache))}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </Paper>
  );
});

/* -------------------------------------------------------------------------- */
/*  List (summary + filters + cards)                                           */
/* -------------------------------------------------------------------------- */

const INITIAL_FILTERS = { search: '', user: null, status: '', type: '', urgentOnly: false, sort: 'urgent' };

const TacheList = ({
  lst_tache,
  lst_user,
  first_load,
  loading,
  onShowForm,
  onShowDetails,
  onUpdateTache,
  onAskDelete,
}) => {
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [visible, setVisible] = useState(PAGE_SIZE);

  // derived data: always in sync with the list, no stale filtered copy in state
  const filtered = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    const list = lst_tache.filter((t) => {
      const users = Array.isArray(t.users) ? t.users : [];
      if (filters.user && !users.some((u) => u.idUser === filters.user.idUser)) return false;
      if (filters.status && t.status !== filters.status) return false;
      if (filters.type && t.type !== filters.type) return false;
      if (filters.urgentOnly && !(t.is_urgent && isOpen(t))) return false;
      if (q) {
        const hay = [
          t.idTache,
          t.description,
          t.commande?.ref,
          TYPE_BY_VALUE[t.type]?.label,
          t.createdBy,
          ...users.map(fullName),
        ]
          .join(' ')
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });

    const byDate = (a, b) => new Date(b.created_at) - new Date(a.created_at);
    return list.sort((a, b) => {
      if (filters.sort === 'old') return -byDate(a, b);
      if (filters.sort === 'urgent') {
        const score = (t) => (isOverdue(t) ? 2 : t.is_urgent && isOpen(t) ? 1 : 0);
        return score(b) - score(a) || byDate(a, b);
      }
      return byDate(a, b);
    });
  }, [lst_tache, filters]);

  useEffect(() => setVisible(PAGE_SIZE), [filters]);

  const hasFilters = JSON.stringify(filters) !== JSON.stringify(INITIAL_FILTERS);
  const patch = (p) => setFilters((f) => ({ ...f, ...p }));

  return (
    <Box>
      <Summary tasks={lst_tache} filters={filters} setFilters={setFilters} loading={first_load} />

      <Grid container spacing={1.5} mb={2} alignItems="center">
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            size="small"
            placeholder="Rechercher (n°, description, commande, personne…)"
            value={filters.search}
            onChange={(e) => patch({ search: e.target.value })}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <Autocomplete
            size="small"
            value={filters.user}
            onChange={(_, v) => patch({ user: v })}
            options={lst_user}
            isOptionEqualToValue={(o, v) => o?.idUser === v?.idUser}
            getOptionLabel={fullName}
            renderInput={(params) => <TextField {...params} label="Utilisateur" />}
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <TextField
            select
            fullWidth
            size="small"
            label="Type"
            value={filters.type}
            onChange={(e) => patch({ type: e.target.value })}
          >
            <MenuItem value="">Tous</MenuItem>
            {typeTaches.map((t) => (
              <MenuItem key={t.value} value={t.value}>
                {t.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={6} md={2}>
          <TextField
            select
            fullWidth
            size="small"
            label="Trier par"
            value={filters.sort}
            onChange={(e) => patch({ sort: e.target.value })}
          >
            {SORTS.map((s) => (
              <MenuItem key={s.value} value={s.value}>
                {s.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={6} md={2}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => onShowForm(null)}
          >
            Nouvelle tâche
          </Button>
        </Grid>
      </Grid>

      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
        <Typography variant="body2" color="text.secondary" aria-live="polite">
          {filtered.length} tâche{filtered.length > 1 ? 's' : ''}
          {hasFilters ? ` sur ${lst_tache.length}` : ''}
        </Typography>
        {hasFilters && (
          <Button
            size="small"
            color="inherit"
            startIcon={<FilterAltOffOutlinedIcon />}
            onClick={() => setFilters(INITIAL_FILTERS)}
          >
            Effacer les filtres
          </Button>
        )}
      </Stack>

      {first_load ? (
        <Stack spacing={1.5}>
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} variant="rounded" height={118} />
          ))}
        </Stack>
      ) : filtered.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{ p: 6, textAlign: 'center', borderRadius: 2, borderStyle: 'dashed' }}
        >
          <InboxOutlinedIcon sx={{ fontSize: 44, color: 'text.disabled' }} />
          <Typography variant="h6" mt={1}>
            {hasFilters ? 'Aucune tâche ne correspond' : 'Aucune tâche pour le moment'}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            {hasFilters
              ? 'Modifiez ou effacez les filtres pour voir plus de résultats.'
              : 'Créez la première tâche pour l’assigner à votre équipe.'}
          </Typography>
          {hasFilters ? (
            <Button onClick={() => setFilters(INITIAL_FILTERS)}>Effacer les filtres</Button>
          ) : (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => onShowForm(null)}>
              Nouvelle tâche
            </Button>
          )}
        </Paper>
      ) : (
        <Stack spacing={1.5} sx={{ opacity: loading ? 0.7 : 1, transition: 'opacity .2s' }}>
          {filtered.slice(0, visible).map((tache) => (
            <TacheCard
              key={tache.idTache}
              tache={tache}
              onShowDetails={onShowDetails}
              onShowForm={onShowForm}
              onUpdateTache={onUpdateTache}
              onAskDelete={onAskDelete}
            />
          ))}
          {visible < filtered.length && (
            <Box textAlign="center" pt={1}>
              <Button onClick={() => setVisible((v) => v + PAGE_SIZE)}>
                Afficher plus ({filtered.length - visible} restantes)
              </Button>
            </Box>
          )}
        </Stack>
      )}
    </Box>
  );
};

export default TacheListContainer;